import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { FileText, Upload } from "lucide-react";

const FIELDS = [
  ["academic_year", "Anno accademico", "text"], ["fee", "Quota annuale (€)", "number"], ["discount_pct", "Sconto unica soluzione (%)", "number"],
  ["installments", "Numero rate", "number"], ["monthly_amount", "Rata mensile (€)", "number"], ["couple_fee", "Quota coniuge in coppia (€)", "number"],
  ["iban", "IBAN", "text"], ["account_holder", "Intestatario del conto", "text"], ["bank_name", "Banca (facoltativo)", "text"],
  ["paypal_link", "Link PayPal (es. paypal.me/…)", "url"], ["card_link", "Link pagamento con carta", "url"],
  ["payment_reason", "Causale ({anno} {cognome} {nome})", "text"], ["notify_email", "Email segreteria per avvisi", "email"], ["privacy_email", "Email per revoca consensi", "email"], ["director_name", "Direttore", "text"],
];

export function EnrollmentSettingsDialog({ open, onOpenChange }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["enr-settings"], queryFn: async () => (await api.get("/enrollments/settings")).data, enabled: open });
  const [f, setF] = useState({});
  useEffect(() => { if (data) setF(data); }, [data]);
  const save = useMutation({
    mutationFn: async () => {
      const body = Object.fromEntries(FIELDS.map(([k, , t]) => [k, t === "number" ? Number(f[k]) : f[k]]));
      return (await api.put("/enrollments/settings", { ...body, open: !!f.open })).data;
    },
    onSuccess: (d) => { qc.setQueryData(["enr-settings"], d); toast.success("Impostazioni salvate"); onOpenChange(false); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { const r = await api.post("/enrollments/settings/regolamento", fd); setF(r.data); qc.setQueryData(["enr-settings"], r.data); toast.success("Regolamento caricato"); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="enr-settings-dialog">
        <DialogHeader><DialogTitle>Impostazioni iscrizione modulare</DialogTitle></DialogHeader>
        <label className="flex items-center justify-between rounded-lg border border-stone-200 px-3 py-2.5 text-sm">
          Iscrizioni aperte <Switch checked={!!f.open} onCheckedChange={(v) => setF({ ...f, open: v })} data-testid="enr-set-open"/>
        </label>
        <div className="grid sm:grid-cols-2 gap-3">
          {FIELDS.map(([k, l, t]) => (
            <div key={k} className={["iban", "payment_reason"].includes(k) ? "sm:col-span-2" : ""}>
              <Label className="text-xs">{l}</Label>
              <Input type={t} value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1" data-testid={`enr-set-${k}`}/>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between rounded-lg border border-stone-200 px-3 py-2.5 text-sm">
          <span className="inline-flex items-center gap-2"><FileText size={14}/> {f.regolamento_name || "Regolamento non caricato"}</span>
          <label className="text-xs px-3 py-1.5 rounded-full border border-stone-300 cursor-pointer hover:border-[#1C352D] inline-flex items-center gap-1">
            <Upload size={12}/> Carica PDF
            <input type="file" accept="application/pdf" className="hidden" data-testid="enr-set-regolamento" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}/>
          </label>
        </div>
        <Button onClick={() => save.mutate()} disabled={save.isPending} className="bg-[#1C352D] hover:bg-[#264A3E]" data-testid="enr-set-save">Salva</Button>
      </DialogContent>
    </Dialog>
  );
}
